// ── participant-self-edit: the FR-PART-003 safe-field allowlist ─────────────
// Binding decision (2026-07-01): participants may edit exactly name / phone /
// timezone / notification_preferences on their OWN profile. This module is the
// client-side mirror of the server allowlist; the server remains authoritative.
//
// Two lists, fail-closed:
//   1. PARTICIPANT_SELF_EDIT_ALLOWLIST — the safe fields a participant MAY submit
//      on their own profile. A field is editable ONLY if it is explicitly here
//      AND not in the hard-exclusion list.
//   2. PARTICIPANT_SELF_EDIT_HARD_EXCLUSIONS — fields a participant can NEVER
//      self-edit, regardless of any future allowlist change. These carry
//      authorization / scope / outcome meaning (company, cohort, program
//      assignment, role, access scope, report visibility, capstone evaluation/
//      status, certificate status). This client guard ensures a participant can
//      only ever SUBMIT allowlisted safe fields (defence-in-depth, blueprint §6
//      participant isolation).

import type { ProfileUpdateRequest } from "../services/coaching-dtos";

/** The keys the PATCH /profile contract accepts (read-side reference only). */
export type ProfileSelfEditField = keyof ProfileUpdateRequest;

/**
 * The APPROVED safe-field allowlist for participant self-edit.
 *
 * The binding-approved list. A field in the hard-exclusion set below can NEVER
 * be added — `participantSelfEditableFields()` filters it out defensively.
 */
export const PARTICIPANT_SELF_EDIT_ALLOWLIST: readonly ProfileSelfEditField[] = [
  "name",
  "phone",
  "timezone",
  "notification_preferences"
];

/**
 * Fields a participant can NEVER self-edit (authorization / scope / outcome).
 * These are not all PATCH /profile keys — they are the conceptual fields the
 * release gate forbids a participant from setting on themselves through ANY
 * self-edit path. The guard rejects them even if a future allowlist change tried
 * to include one. (The server is the source of truth and re-checks every field.)
 */
export const PARTICIPANT_SELF_EDIT_HARD_EXCLUSIONS: readonly string[] = [
  "company",
  "company_id",
  "companyId",
  "cohort",
  "cohort_id",
  "cohortId",
  "program",
  "program_id",
  "programId",
  "program_assignment",
  "role",
  "role_key",
  "access_scope",
  "scope",
  "report_visibility",
  "reportVisibility",
  "artifact_visibility",
  "capstone",
  "capstone_status",
  "capstone_evaluation",
  "evaluation",
  "certificate",
  "certificate_status",
  "email",
  "title",
  "status"
];

const hardExclusionSet = new Set(PARTICIPANT_SELF_EDIT_HARD_EXCLUSIONS);

/** Whether a field is hard-excluded from participant self-edit (never editable). */
export function isHardExcludedSelfEditField(field: string): boolean {
  return hardExclusionSet.has(field);
}

/**
 * The fields a participant may actually self-edit right now: the allowlist MINUS
 * anything hard-excluded.
 */
export function participantSelfEditableFields(): ProfileSelfEditField[] {
  return PARTICIPANT_SELF_EDIT_ALLOWLIST.filter((field) => !isHardExcludedSelfEditField(field));
}

/** Self-edit is enabled when at least one approved safe field is available. */
export function isParticipantSelfEditEnabled(): boolean {
  return participantSelfEditableFields().length > 0;
}

/**
 * The client guard a participant self-edit PATCH MUST pass through before it is
 * sent. It returns ONLY the allowlisted safe fields from the proposed body,
 * dropping every hard-excluded / non-allowlisted key and reporting which keys
 * were rejected. A sensitive field can NEVER be set this way.
 */
export interface SelfEditGuardResult {
  /** The body to actually send — only approved, non-excluded fields survive. */
  safe: Partial<ProfileUpdateRequest>;
  /** Keys that were stripped because they are not allowlisted (or are excluded). */
  rejected: string[];
  /** Keys that were stripped specifically because they are hard-excluded. */
  hardExcluded: string[];
}

export function guardParticipantSelfEdit(proposed: Record<string, unknown>): SelfEditGuardResult {
  const allowed = new Set<string>(participantSelfEditableFields());
  const safe: Partial<ProfileUpdateRequest> = {};
  const rejected: string[] = [];
  const hardExcluded: string[] = [];

  for (const key of Object.keys(proposed)) {
    if (isHardExcludedSelfEditField(key)) {
      hardExcluded.push(key);
      rejected.push(key);
      continue;
    }
    if (allowed.has(key)) {
      (safe as Record<string, unknown>)[key] = proposed[key];
    } else {
      rejected.push(key);
    }
  }

  return { safe, rejected, hardExcluded };
}
